import React, { useCallback, useEffect, useMemo, useState } from 'react';
import propTypes from 'prop-types';
import { useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { CircularProgress } from '@mui/material';
import { TriangleAlert } from 'lucide-react';
import Button from 'tailwind-components/Button/Button';
import { cn } from 'lib/utils';
import FeaturedCarrierCard from './FeaturedCarrierCard';
import CommissionAtRiskCard from './CommissionAtRiskCard';
import CoverageGapCard from './CoverageGapCard';
import PartialRecommendationsNotice from './PartialRecommendationsNotice';
import { agencyRecommendationsKeys } from './api/agencyRecommendations';
import useFeaturedCarrierMissingAppointments, {
  mergeFeaturedCarrierResults
} from './useFeaturedCarrierMissingAppointments';
import useAgencyRecommendations from './useAgencyRecommendations';
import {
  agentRecommendationContact,
  coverageGapRecommendationIds,
  coverageGapRecommendationKey,
  policyRecommendationIds,
  policyRecommendationKey,
  sendAgencyRecommendationEmail,
  saveNotAppointedPolicyRecommendation,
  saveTopSellerMissingAppointmentsRecommendation
} from './agencyRecommendationTracking';

function AgencyRecommendationsSection({
  agencies,
  filterAgencyIds,
  className
}) {
  const mainZohoId = useSelector(
    state => state.user.mainZohoId || state.user.zohoData.id || ''
  );
  const safeAgencies = useMemo(
    () => (Array.isArray(agencies) ? agencies : []),
    [agencies]
  );

  const {
    loading: loadingFeatured,
    results: featuredResults,
    highlightedCarrier,
    failedAgencies: featuredFailedAgencies,
    retryFailed: retryFeaturedFailed
  } = useFeaturedCarrierMissingAppointments(safeAgencies);

  const featuredData = useMemo(
    () => mergeFeaturedCarrierResults(featuredResults, filterAgencyIds),
    [featuredResults, filterAgencyIds]
  );

  const {
    loadingPolicies,
    policies,
    loadingStateLeaders,
    coverageGaps,
    coverageGapsPartialMeta,
    failedAgencies: recsFailedAgencies,
    retryFailed: retryRecsFailed
  } = useAgencyRecommendations(safeAgencies);

  const queryClient = useQueryClient();

  const failedAgencies = useMemo(() => {
    const seen = new Set();
    const combined = [];
    for (const a of [...recsFailedAgencies, ...featuredFailedAgencies]) {
      if (seen.has(a.agencyId)) continue;
      seen.add(a.agencyId);
      combined.push(a);
    }
    return combined;
  }, [recsFailedAgencies, featuredFailedAgencies]);

  // Carousel state: each bottom column shows one card at a time.
  const [policiesIndex, setPoliciesIndex] = useState(0);
  const [gapsIndex, setGapsIndex] = useState(0);
  const [dismissedPolicyKeys, setDismissedPolicyKeys] = useState(
    () => new Set()
  );
  const [dismissedGapKeys, setDismissedGapKeys] = useState(() => new Set());
  const [sendingPolicy, setSendingPolicy] = useState(false);
  const [sendingGap, setSendingGap] = useState(false);

  const agenciesKey = useMemo(
    () => safeAgencies.map(a => a.agencyId).join(','),
    [safeAgencies]
  );

  // Reset dismissed items and index when data or agency changes.
  useEffect(() => {
    setDismissedPolicyKeys(new Set());
    setPoliciesIndex(0);
  }, [policies, agenciesKey]);
  useEffect(() => {
    setDismissedGapKeys(new Set());
    setGapsIndex(0);
  }, [coverageGaps, agenciesKey]);

  const visiblePolicies = useMemo(
    () =>
      policies.filter(
        p =>
          !dismissedPolicyKeys.has(policyRecommendationKey(p)) &&
          (!filterAgencyIds || filterAgencyIds.has(String(p.agencyId)))
      ),
    [policies, dismissedPolicyKeys, filterAgencyIds]
  );
  const visibleGaps = useMemo(
    () =>
      coverageGaps.filter(
        g =>
          !dismissedGapKeys.has(coverageGapRecommendationKey(g)) &&
          (!filterAgencyIds || filterAgencyIds.has(String(g.agencyId)))
      ),
    [coverageGaps, dismissedGapKeys, filterAgencyIds]
  );

  const policiesCount = visiblePolicies.length;
  const gapsCount = visibleGaps.length;

  const advancePolicies = useCallback(() => {
    setPoliciesIndex(i => (policiesCount > 0 ? (i + 1) % policiesCount : 0));
  }, [policiesCount]);
  const retreatPolicies = useCallback(() => {
    setPoliciesIndex(i =>
      policiesCount > 0 ? (i - 1 + policiesCount) % policiesCount : 0
    );
  }, [policiesCount]);
  const advanceGaps = useCallback(() => {
    setGapsIndex(i => (gapsCount > 0 ? (i + 1) % gapsCount : 0));
  }, [gapsCount]);

  const safePoliciesIndex =
    policiesCount > 0 ? policiesIndex % policiesCount : 0;
  const safeGapsIndex = gapsCount > 0 ? gapsIndex % gapsCount : 0;

  const handleSuggestPolicy = useCallback(async () => {
    const policy = visiblePolicies[safePoliciesIndex];
    if (!policy || sendingPolicy) return;

    const { agentName, agentEmail, agentNpn } =
      agentRecommendationContact(policy);
    const carrierName = policy?.carrierName || policy?.carrier_name || '';
    const state = policy?.state ? String(policy.state).trim() : '';

    setSendingPolicy(true);
    try {
      const sent = await sendAgencyRecommendationEmail({
        agentName,
        mainZohoId,
        agencyName: policy?.agencyName || '',
        carrierName,
        state,
        npn: agentNpn,
        agentEmail
      });
      if (!sent) return;

      // the card leaves the carousel immediately, then the refetch reconciles it with
      // what the api now considers already-recommended. awaiting the save first is
      // what makes that refetch see this suggestion instead of racing it.
      const key = policyRecommendationKey(policy);
      setDismissedPolicyKeys(prev => new Set([...prev, key]));

      if (policy?.agencyId) {
        const { policyId, agentId, carrierId } =
          policyRecommendationIds(policy);
        if (policyId && agentId) {
          await saveNotAppointedPolicyRecommendation({
            agencyId: policy.agencyId,
            policyId,
            agentId,
            carrierId,
            state
          });
        }
      }
      queryClient.invalidateQueries({
        queryKey: agencyRecommendationsKeys.notAppointedPoliciesAll()
      });
    } finally {
      setSendingPolicy(false);
    }
  }, [
    visiblePolicies,
    safePoliciesIndex,
    mainZohoId,
    sendingPolicy,
    queryClient
  ]);

  const handleSuggestGap = useCallback(async () => {
    const gap = visibleGaps[safeGapsIndex];
    if (!gap || sendingGap) return;

    const { agentName, agentEmail, agentNpn } = agentRecommendationContact(gap);
    const carrierName = gap?.carrierName || '';
    const { carrierId, agentId, state } = coverageGapRecommendationIds(gap);

    setSendingGap(true);
    try {
      const sent = await sendAgencyRecommendationEmail({
        agentName,
        mainZohoId,
        agencyName: gap?.agencyName || '',
        carrierName,
        state: state || '',
        npn: agentNpn,
        agentEmail
      });
      if (!sent) return;

      // with up to 3 leaders per agency, suppressing one carrier no longer empties the
      // list: refetching after the save is what brings the next leader into view
      const key = coverageGapRecommendationKey(gap);
      setDismissedGapKeys(prev => new Set([...prev, key]));

      if (gap?.agencyId && carrierId && agentId) {
        await saveTopSellerMissingAppointmentsRecommendation({
          agencyId: gap.agencyId,
          carrierId,
          entries: [{ agentId, state }]
        });
      }
      queryClient.invalidateQueries({
        queryKey: agencyRecommendationsKeys.stateLeadersAll()
      });
    } finally {
      setSendingGap(false);
    }
  }, [visibleGaps, safeGapsIndex, mainZohoId, sendingGap, queryClient]);

  const retreatGaps = useCallback(() => {
    setGapsIndex(i => (gapsCount > 0 ? (i - 1 + gapsCount) % gapsCount : 0));
  }, [gapsCount]);

  const currentPolicy = visiblePolicies[safePoliciesIndex];
  const currentGap = visibleGaps[safeGapsIndex];

  if (safeAgencies.length === 0) {
    return null;
  }

  const showFeatured = !!highlightedCarrier;

  let commissionAtRiskCard = (
    <EmptyRecommendationPlaceholder
      badge='Commission at risk'
      badgeClassName='bg-primary-light text-primary-darkened'
      message='No commissions at risk found.'
    />
  );
  if (loadingPolicies && visiblePolicies.length === 0) {
    commissionAtRiskCard = (
      <RecommendationLoadingPlaceholder label='Loading commission risks…' />
    );
  } else if (currentPolicy) {
    const policyKey =
      currentPolicy.policyId ??
      `${currentPolicy.agentId}-${currentPolicy.carrierId}-${currentPolicy.createdAt}`;
    commissionAtRiskCard = (
      <CommissionAtRiskCard
        key={`${currentPolicy.agencyId || ''}::${policyKey}`}
        policy={currentPolicy}
        onNext={advancePolicies}
        onPrev={retreatPolicies}
        onSuggest={handleSuggestPolicy}
        suggestLoading={sendingPolicy}
        position={safePoliciesIndex + 1}
        total={policiesCount}
      />
    );
  }

  let coverageGapCard = (
    <EmptyRecommendationPlaceholder
      badge='Coverage gap'
      badgeClassName='bg-[#ede9fe] text-[#6d28d9]'
      message='No coverage gaps detected.'
    />
  );
  if (loadingStateLeaders && visibleGaps.length === 0) {
    coverageGapCard = (
      <RecommendationLoadingPlaceholder label='Loading coverage gaps…' />
    );
  } else if (currentGap) {
    coverageGapCard = (
      <CoverageGapCard
        key={`${currentGap.agencyId || ''}::${currentGap.agentId}::${
          currentGap.state
        }::${currentGap.carrierId ?? currentGap.carrierName}`}
        gap={currentGap}
        onNext={advanceGaps}
        onPrev={retreatGaps}
        onSuggest={handleSuggestGap}
        suggestLoading={sendingGap}
        position={safeGapsIndex + 1}
        total={gapsCount}
      />
    );
  }

  return (
    <section className={cn('flex w-full min-w-0 flex-col gap-3', className)}>
      <header className='flex flex-col gap-1 mt-2'>
        <h3 className='m-0 text-base font-semibold text-primary-darkened sm:text-lg'>
          Recommendations for your agency
        </h3>
        <p className='m-0 text-sm leading-snug text-grey'>
          Suggested appointments based on your agents&rsquo; sales activity and
          coverage gaps
        </p>
      </header>

      {failedAgencies.length > 0 &&
        !loadingFeatured &&
        !loadingPolicies &&
        !loadingStateLeaders && (
          <div className='flex items-center gap-2 rounded-lg border border-semantic-danger/30 bg-semantic-danger/5 px-3 py-2'>
            <TriangleAlert className='size-4 shrink-0 text-semantic-danger' />
            <p className='m-0 flex-1 text-xs text-semantic-danger'>
              Failed to load recommendations for:{' '}
              <span className='font-semibold'>
                {failedAgencies.map(a => a.agencyName).join(', ')}
              </span>
            </p>
            <Button
              variant='secondary'
              size='xsmall'
              text='Retry'
              onClick={() => {
                retryRecsFailed();
                retryFeaturedFailed();
              }}
              className='shrink-0'
            />
          </div>
        )}

      {showFeatured && (
        <>
          <FeaturedCarrierCard
            highlightedCarrier={highlightedCarrier}
            data={featuredData}
            loading={loadingFeatured}
            agencyId={safeAgencies[0]?.agencyId}
            agencyName={safeAgencies[0]?.agencyName}
            mainZohoId={mainZohoId}
          />
          {!loadingFeatured && (
            <PartialRecommendationsNotice
              partial={featuredData?.partial}
              skippedAgents={featuredData?.skippedAgents}
            />
          )}
        </>
      )}

      <div className='grid grid-cols-1 gap-3 lg:grid-cols-2'>
        <div className='flex min-w-0 flex-col gap-2'>
          {commissionAtRiskCard}
        </div>
        <div className='flex min-w-0 flex-col gap-2'>
          {!loadingStateLeaders && (
            <PartialRecommendationsNotice
              partial={coverageGapsPartialMeta?.partial}
              skippedAgents={coverageGapsPartialMeta?.skippedAgents}
            />
          )}
          {coverageGapCard}
        </div>
      </div>
    </section>
  );
}

function EmptyRecommendationPlaceholder({ badge, badgeClassName, message }) {
  return (
    <div
      className={cn(
        'flex min-h-[120px] w-full min-w-0 flex-col gap-3 overflow-hidden rounded-[10px]',
        'border border-dashed border-grey-lighter bg-white p-4'
      )}
    >
      <span
        className={cn(
          'inline-flex w-fit items-center rounded-full px-2.5 py-1',
          'text-[10px] font-bold tracking-[0.08em] uppercase',
          badgeClassName
        )}
      >
        {badge}
      </span>
      <div className='flex flex-1 items-center justify-center'>
        <span className='text-sm text-grey'>{message}</span>
      </div>
    </div>
  );
}

EmptyRecommendationPlaceholder.propTypes = {
  badge: propTypes.string.isRequired,
  badgeClassName: propTypes.string.isRequired,
  message: propTypes.string.isRequired
};

function RecommendationLoadingPlaceholder({ label }) {
  return (
    <div
      className={cn(
        'flex min-h-[120px] w-full min-w-0 items-center justify-center gap-2',
        'rounded-[10px] border border-dashed border-grey-lighter bg-white p-4 text-grey'
      )}
    >
      <CircularProgress size={16} thickness={5} />
      <span className='text-sm'>{label}</span>
    </div>
  );
}

RecommendationLoadingPlaceholder.propTypes = {
  label: propTypes.string.isRequired
};

AgencyRecommendationsSection.propTypes = {
  agencies: propTypes.arrayOf(
    propTypes.shape({
      agencyId: propTypes.string.isRequired,
      agencyName: propTypes.string.isRequired
    })
  ),
  filterAgencyIds: propTypes.instanceOf(Set),
  className: propTypes.string
};

export default AgencyRecommendationsSection;
